import React, { useState, useEffect } from 'react';
import { X, CreditCard, AlertCircle, CheckCircle } from 'lucide-react';
import { createCheckoutSession, updateSubscription } from '../../../utils/stripe/api';
import { SUBSCRIPTION_PLANS } from '../../../utils/stripe/config';
import { useAiCreditsStore } from '../../../stores/aiCreditsStore';
import { supabase } from '../../../utils/supabase';

// Define base URL for redirects
const baseURL = window.location.origin;

import { useAuthStore } from '../../../stores/authStore';
import type { SubscriptionPlanType } from '../../../types/subscriptionPlan';

interface SubscriptionModalProps {
  plan: SubscriptionPlanType;
  currentPlanId: string | null;
  currentSubscriptionId: string | null;
  onClose: () => void;
}

const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due'];

async function resolveActiveSubscription(userId: string): Promise<{
  subscriptionId: string | null;
  planId: string | null;
  nextPlanId: string | null;
}> {
  const { data: customer } = await supabase
    .from('stripe_customers')
    .select('customer_id')
    .eq('user_id', userId)
    .maybeSingle();

  if (!customer?.customer_id) {
    return { subscriptionId: null, planId: null, nextPlanId: null };
  }

  const { data: subscription } = await supabase
    .from('stripe_subscriptions')
    .select('subscription_id, status, price_id')
    .eq('customer_id', customer.customer_id)
    .maybeSingle();

  if (
    !subscription?.subscription_id ||
    !ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status)
  ) {
    return { subscriptionId: null, planId: null, nextPlanId: null };
  }

  const { data: userRow } = await supabase
    .from('users')
    .select('plan, next_plan')
    .eq('id', userId)
    .maybeSingle();

  const planFromUser =
    userRow?.plan && userRow.plan !== 'unsubscribed' ? userRow.plan : null;
  const planFromStripe = SUBSCRIPTION_PLANS.find(
    (p) => p.priceId === subscription.price_id
  )?.id ?? null;

  return {
    subscriptionId: subscription.subscription_id,
    planId: planFromUser ?? planFromStripe,
    nextPlanId: userRow?.next_plan ?? null,
  };
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ 
  plan, 
  currentPlanId,
  currentSubscriptionId,
  onClose 
}) => {
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [planChangeType, setPlanChangeType] = useState<'upgrade' | 'downgrade' | 'same' | 'new'>('new');
  const [resolvedSubscriptionId, setResolvedSubscriptionId] = useState(currentSubscriptionId);
  const [resolvedPlanId, setResolvedPlanId] = useState(currentPlanId);
  const [resolvedNextPlanId, setResolvedNextPlanId] = useState<string | null>(null);
  const { user } = useAuthStore();
  const { loadCredits } = useAiCreditsStore();

  useEffect(() => {
    setResolvedSubscriptionId(currentSubscriptionId);
    setResolvedPlanId(currentPlanId);
  }, [currentSubscriptionId, currentPlanId]);

  useEffect(() => {
    if (!user?.id) return;

    const refreshSubscriptionState = async () => {
      try {
        const resolved = await resolveActiveSubscription(user.id);
        if (resolved.subscriptionId) {
          setResolvedSubscriptionId(resolved.subscriptionId);
        }
        if (resolved.planId) {
          setResolvedPlanId(resolved.planId);
        }
        setResolvedNextPlanId(resolved.nextPlanId);
      } catch (refreshError) {
        console.error('Failed to refresh subscription state:', refreshError);
      }
    };

    refreshSubscriptionState();
  }, [user?.id]);

  // Determine if this is an upgrade, downgrade, or same plan
  useEffect(() => {
    if (resolvedNextPlanId && resolvedNextPlanId === plan.id && resolvedPlanId !== plan.id) {
      setPlanChangeType('same');
      return;
    }

    if (!resolvedPlanId) {
      setPlanChangeType('new');
      return;
    }
    
    const currentPlan = SUBSCRIPTION_PLANS.find(p => p.id === resolvedPlanId);
    if (!currentPlan) {
      setPlanChangeType('new');
      return;
    }
    
    if (currentPlan.price > plan.price) {
      setPlanChangeType('downgrade');
    } else if (currentPlan.price < plan.price) {
      setPlanChangeType('upgrade');
    } else {
      setPlanChangeType('same');
    }
  }, [resolvedPlanId, resolvedNextPlanId, plan]);


  const resolveChangeType = (
    effectivePlanId: string | null,
    nextPlanId: string | null
  ): 'upgrade' | 'downgrade' | 'same' | 'new' => {
    if (nextPlanId && nextPlanId === plan.id && effectivePlanId !== plan.id) {
      return 'same';
    }

    const currentPlan = effectivePlanId
      ? SUBSCRIPTION_PLANS.find((p) => p.id === effectivePlanId)
      : null;

    if (!currentPlan) return 'new';
    if (currentPlan.price > plan.price) return 'downgrade';
    if (currentPlan.price < plan.price) return 'upgrade';
    return 'same';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.email || !user?.id) {
      setError('ユーザー情報が取得できませんでした。再度ログインしてください。');
      return;
    }
    
    setError(null);
    setIsProcessing(true);
    
    let subscriptionId = resolvedSubscriptionId;
    let effectivePlanId = resolvedPlanId;
    let effectiveNextPlanId = resolvedNextPlanId;

    if (!subscriptionId) {
      const resolved = await resolveActiveSubscription(user.id);
      subscriptionId = resolved.subscriptionId;
      effectivePlanId = resolved.planId ?? effectivePlanId;
      effectiveNextPlanId = resolved.nextPlanId ?? effectiveNextPlanId;
      if (subscriptionId) {
        setResolvedSubscriptionId(subscriptionId);
      }
      if (effectivePlanId) {
        setResolvedPlanId(effectivePlanId);
      }
      setResolvedNextPlanId(effectiveNextPlanId);
    }

    if (subscriptionId) {
      const changeType = resolveChangeType(effectivePlanId, effectiveNextPlanId);

      if (changeType === 'same') {
        setError('すでに同じプランに加入しています');
        setIsProcessing(false);
        return;
      }
      
      try {
        const isUpgrade = changeType === 'upgrade';
        const result = await updateSubscription(
          subscriptionId,
          plan.id,
          plan.priceId,
          isUpgrade,
          user.id
        );
        
        if (result) {
          if (isUpgrade) {
            await loadCredits();
          }
          setIsComplete(true);

          if (isUpgrade) {
            setSuccessMessage('プランをアップグレードしました。変更を反映するためにページをリロードします。');
            setTimeout(() => {
              window.location.assign('/account');
            }, 2000);
          } else {
            setSuccessMessage('プランの変更を受け付けました。次回の請求日から適用されます。');
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          }
        }
        
      } catch (updateError) {
        console.error('Subscription update error:', updateError);
        setError(updateError instanceof Error ? updateError.message : '決済処理に失敗しました');
        setIsProcessing(false);
      }
    } else {
      try {
        const session = await createCheckoutSession(
          plan.priceId,
          'subscription', 
          user.id,
          {
            plan_id: plan.id,
            user_email: user.email
          },
          1,
          `${baseURL}/stripe-success?session_id={CHECKOUT_SESSION_ID}`,
          `${baseURL}/stripe-cancel`
        );

        if (session?.url) {
          window.location.assign(session.url);
        } else {
          throw new Error('チェックアウトセッションの作成に失敗しました');
        }
      } catch (checkoutError) {
        console.error('Payment error:', checkoutError);
        setError(checkoutError instanceof Error ? checkoutError.message : '決済処理に失敗しました');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  // Success message display
  if (isComplete) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {successMessage || 'プラン変更が完了しました'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {planChangeType === 'downgrade'
                ? "次回の請求日からプランが変更されます。" 
                : "プランがアップグレードされました。"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">サブスクリプション登録</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isProcessing}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <h4 className="font-medium text-lg">{plan.name}</h4>
          <p className="text-2xl font-bold text-blue-600">
            ¥{plan.price.toLocaleString()}/月
          </p>
          <p className="text-sm text-gray-600 mt-2">
            AI生成回数: {plan.aiLimit}回/月
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <span className="text-green-700 text-sm">{successMessage}</span>
          </div>
        )}

          <button
            onClick={handleSubmit}
            className={`w-full py-3 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
              planChangeType === 'same' 
                ? 'bg-gray-400 text-white' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
            title={planChangeType === 'downgrade' ? "次回の請求日からプランが変更されます" : ""}
            disabled={isProcessing || planChangeType === 'same'}
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                処理中...
              </>
            ) : planChangeType === 'same' ? (
              `現在のプランと同じです`
            ) : planChangeType === 'downgrade' ? (
              `次回の請求日から¥${plan.price.toLocaleString()}/月に変更する`
            ) : currentSubscriptionId ? (
              `¥${plan.price.toLocaleString()}/月に変更する`
            ) : resolvedSubscriptionId ? (
              `¥${plan.price.toLocaleString()}/月に変更する`
            ) : (
              `¥${plan.price.toLocaleString()}/月で登録する`
            )}
          </button>

        <p className="text-xs text-gray-500 mt-4 text-center">
          {(currentSubscriptionId || resolvedSubscriptionId)
            ? "プラン変更後も、いつでもキャンセルできます。" 
            : "登録後、いつでもキャンセルできます。"}
        </p>
        
        {planChangeType === 'downgrade' && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-700">
              <strong>ダウングレードについて:</strong> プランの変更は次回の請求日から適用されます。
              現在の請求期間中は現在のプランの機能をそのままご利用いただけます。
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionModal;